/**
 * Service Statistics Widget
 *
 * Displays aggregate statistics about services (counts by status and tier).
 * Supports an `all` / `my-team` scope toggle (issue #1259), mirroring
 * `TechnologyStatisticsWidget`'s scope pattern (#491) but hosting the toggle
 * inside the shared `WidgetConfigDialog` (#1258) behind a gear `headerAction`
 * rather than painting it inline in the widget body.
 */

import { useState } from 'react';
import { useServiceStats, useMyServices } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { WidgetConfigDialog } from './WidgetConfigDialog';
import { useStatisticsWidget } from './useStatisticsWidget';
import { StatusBadge } from '../shared/StatusBadge';
import type { WidgetConfig } from './types';
import type { Service } from '../../graphql/types';
import '@tylertech/forge/icon';
import '@tylertech/forge/icon-button';
import '@tylertech/forge/button';
import './ServiceStatisticsWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'service-statistics',
  title: 'Service Statistics',
  size: 'medium',
  refreshInterval: 30000, // 30 seconds
  showRefresh: true,
  showExpand: false,
};

/** The widget's two selectable scopes (issue #1259). Persisted in `DashboardWidget.config.scope`. */
export type ServiceWidgetScope = 'all' | 'my-team';

const SCOPE_LABEL: Record<ServiceWidgetScope, string> = {
  all: 'All services',
  'my-team': "My team's services",
};

function isServiceWidgetScope(value: unknown): value is ServiceWidgetScope {
  return value === 'all' || value === 'my-team';
}

/** Reads the widget's scope from its persisted config, defaulting to `'all'` (pre-#1259 behavior). */
function getScope(config: Record<string, unknown> | undefined): ServiceWidgetScope {
  const raw = config?.scope;
  return isServiceWidgetScope(raw) ? raw : 'all';
}

export interface ServiceStatisticsWidgetProps {
  /** This widget's persisted config (`{ scope?: 'all' | 'my-team' }`). */
  config?: Record<string, unknown>;
  /** Called with the widget's full updated config when the scope toggle changes. */
  onConfigChange?: (config: Record<string, unknown>) => void;
}

interface ServiceStats {
  total: number;
  byStatus: Record<string, number>;
  byTier: Record<string, number>;
}

type ServiceStatsData = ReturnType<typeof useServiceStats>['data'];
type MyServicesData = ReturnType<typeof useMyServices>['data'];

/**
 * Normalized data handed to {@link extractServiceStats} — a stable,
 * module-level function per `useStatisticsWidget`'s contract, so the scope
 * that produced the data travels alongside it rather than being captured
 * from a closure.
 */
type ServiceStatisticsSource =
  { scope: 'all'; data: ServiceStatsData } | { scope: 'my-team'; data: MyServicesData };

/**
 * Tallies a raw service list into the widget's stat shape. Used for the
 * `my-team` scope, where the server has no `serviceStats(filter:{teamId})`
 * equivalent scoped to "all of the current user's teams" (issue #1259) —
 * `useMyServices` already resolves that ownership scope, so counting its
 * (bounded) result client-side avoids a backend change.
 */
function aggregateServiceList(services: Service[]): ServiceStats {
  const byStatus: Record<string, number> = {};
  const byTier: Record<string, number> = {};

  for (const s of services) {
    const status = String(s.lifecycleStatus ?? 'unknown');
    /* c8 ignore next -- guard against prototype pollution; ServiceLifecycleStatus enum can never be these values */
    if (status === '__proto__' || status === 'constructor' || status === 'prototype') continue;
    // eslint-disable-next-line security/detect-object-injection -- status is a Service.lifecycleStatus enum value, not user-controlled
    byStatus[status] = (byStatus[status] ?? 0) + 1;
  }

  for (const s of services) {
    const tier = String(s.tier ?? 'unknown');
    /* c8 ignore next -- guard against prototype pollution; ServiceTier enum can never be these values */
    if (tier === '__proto__' || tier === 'constructor' || tier === 'prototype') continue;
    // eslint-disable-next-line security/detect-object-injection -- tier is a Service.tier enum value, not user-controlled
    byTier[tier] = (byTier[tier] ?? 0) + 1;
  }

  return { total: services.length, byStatus, byTier };
}

/**
 * Map the active scope's source data into the widget's stat shape.
 *
 * `all` scope counts are computed server-side via a projected paginated scan
 * (issue #411), so the widget reflects the true total at any scale without
 * transferring full Service items to the browser just to call `.length`
 * (issue #421). `my-team` scope counts the (already-fetched) `myServices`
 * list client-side (issue #1259).
 */
function extractServiceStats(source: ServiceStatisticsSource | undefined): ServiceStats | null {
  if (!source) {
    return null;
  }

  if (source.scope === 'all') {
    if (!source.data?.serviceStats) {
      return null;
    }
    const { total, byStatus, byTier } = source.data.serviceStats;
    return { total, byStatus: byStatus ?? {}, byTier: byTier ?? {} };
  }

  const edges = source.data?.myServices?.edges;
  if (!edges) {
    return null;
  }
  return aggregateServiceList(edges.map((edge) => edge.node));
}

export function ServiceStatisticsWidget({
  config,
  onConfigChange,
}: ServiceStatisticsWidgetProps = {}): JSX.Element {
  const [configOpen, setConfigOpen] = useState(false);
  const scope = getScope(config);

  const serviceStatsResult = useServiceStats();
  // Only relevant when scope === 'my-team'; cheap/cached when scope === 'all' since
  // Apollo dedupes against MyServicesWidget's identical query.
  const myServicesResult = useMyServices();

  // Gate on myServices loading while scoped, so the widget shows its loading
  // skeleton instead of a flash of stale/all-services data before the
  // team-scoped list is ready.
  const scopeReady = scope !== 'my-team' || !myServicesResult.loading;

  const combinedResult = {
    data: scopeReady
      ? scope === 'my-team'
        ? ({ scope: 'my-team', data: myServicesResult.data } as const)
        : ({ scope: 'all', data: serviceStatsResult.data } as const)
      : undefined,
    loading: scope === 'my-team' ? myServicesResult.loading : serviceStatsResult.loading,
    error: scope === 'my-team' ? myServicesResult.error : serviceStatsResult.error,
    refetch: (): unknown => {
      if (scope === 'my-team') {
        return myServicesResult.refetch();
      }
      return serviceStatsResult.refetch();
    },
  };

  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    combinedResult,
    extractServiceStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;
  const IconButtonElement = 'forge-icon-button' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  const handleScopeChange = (next: ServiceWidgetScope): void => {
    if (next === scope) return;
    onConfigChange?.({ ...config, scope: next });
  };

  const scopeSubtext = (
    <p className="service-statistics-widget__scope-label forge-typography--caption">
      {/* scope is narrowed by isServiceWidgetScope() — not an arbitrary user-controlled key */}
      {/* eslint-disable-next-line security/detect-object-injection */}
      Showing: {SCOPE_LABEL[scope]}
    </p>
  );

  const configureAction = onConfigChange && (
    <IconButtonElement
      density="dense"
      onClick={() => setConfigOpen((open) => !open)}
      aria-label="Configure widget"
      data-testid="service-statistics-widget-configure"
    >
      <IconElement name="settings" />
    </IconButtonElement>
  );

  const configDialog = onConfigChange && (
    <WidgetConfigDialog
      open={configOpen}
      title={`Configure ${WIDGET_CONFIG.title}`}
      onClose={() => setConfigOpen(false)}
    >
      {/* Lazy-mount the panel only once opened, matching MyServicesWidget's dialog. */}
      {configOpen && (
        <div data-testid="service-statistics-widget-config-panel">
          <p className="forge-typography--body2">Choose which services this widget counts.</p>
          <div
            className="service-statistics-widget__scope-toggle"
            role="group"
            aria-label="Service scope"
          >
            <ForgeButton
              type="button"
              variant={scope === 'all' ? 'raised' : 'outlined'}
              onClick={() => handleScopeChange('all')}
              data-testid="service-widget-scope-all"
            >
              All
            </ForgeButton>
            <ForgeButton
              type="button"
              variant={scope === 'my-team' ? 'raised' : 'outlined'}
              onClick={() => handleScopeChange('my-team')}
              data-testid="service-widget-scope-my-team"
            >
              My Team
            </ForgeButton>
          </div>
        </div>
      )}
    </WidgetConfigDialog>
  );

  if (!stats) {
    return (
      <>
        <DashboardWidget
          config={WIDGET_CONFIG}
          state={widgetState}
          onRefresh={handleRefresh}
          headerAction={configureAction}
        >
          {scopeSubtext}
          <div className="service-statistics-widget__empty">
            <IconElement name="bar_chart" className="service-statistics-widget__empty-icon" />
            <p className="forge-typography--body2">No statistics available</p>
          </div>
        </DashboardWidget>
        {configDialog}
      </>
    );
  }

  return (
    <>
      <DashboardWidget
        config={WIDGET_CONFIG}
        state={widgetState}
        onRefresh={handleRefresh}
        headerAction={configureAction}
      >
        <div className="service-statistics-widget__content">
          {scopeSubtext}

          {/* Total Count */}
          <div className="service-statistics-widget__total">
            <div className="service-statistics-widget__total-number">{stats.total}</div>
            <div className="service-statistics-widget__total-label forge-typography--label2">
              Total Services
            </div>
          </div>

          {/* By Lifecycle Status */}
          <div className="service-statistics-widget__section">
            <h4 className="service-statistics-widget__section-title forge-typography--label2">
              By Lifecycle Status
            </h4>
            <div className="service-statistics-widget__stats-grid">
              {Object.entries(stats.byStatus)
                .sort(([, a], [, b]) => b - a)
                .map(([status, count]) => (
                  <div key={status} className="service-statistics-widget__stat-item">
                    <StatusBadge variant="lifecycle" value={status} />
                    <span className="service-statistics-widget__stat-count forge-typography--label1">
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* By Tier */}
          <div className="service-statistics-widget__section">
            <h4 className="service-statistics-widget__section-title forge-typography--label2">
              By Business Tier
            </h4>
            <div className="service-statistics-widget__stats-grid">
              {Object.entries(stats.byTier)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([tier, count]) => (
                  <div key={tier} className="service-statistics-widget__stat-item">
                    <StatusBadge variant="tier" value={tier} />
                    <span className="service-statistics-widget__stat-count forge-typography--label1">
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </DashboardWidget>
      {configDialog}
    </>
  );
}
